// Reporting-unit selector. "National" lists every country so the report can be
// pointed at another one; "Regional" lists the admin-1 units of the current
// country and focuses the AI narrative on the chosen region while national
// figures stay on the page.

import { useEffect, useMemo, useState } from "react";
import Select from "react-select";

import { fetchAdminOneUnits, fetchCountries } from "../api";

const SELECT_STYLES = {
  control: (base) => ({ ...base, minHeight: 38, borderColor: "#cfd8e3" }),
  menu: (base) => ({ ...base, zIndex: 20 }),
};

/**
 * @param {object} props
 * @param {{code: string|number, name: string, bounds: object|null}} props.activeCountry
 * @param {"national"|"regional"} props.scope
 * @param {{value: string, label: string, code: number, bounds: object}|null} props.adminOne
 * @param {(patch: {scope?: string, country?: object|null, adminOne?: object|null}) => void} props.onChange
 */
export default function ReportControls({ activeCountry, scope, adminOne, onChange }) {
  const [countries, setCountries] = useState([]);
  const [units, setUnits] = useState([]);
  const [loadingUnits, setLoadingUnits] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchCountries()
      .then((rows) => {
        if (!cancelled) setCountries(rows);
      })
      .catch(() => {
        if (!cancelled) setCountries([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!activeCountry?.code) return undefined;
    let cancelled = false;
    setLoadingUnits(true);
    fetchAdminOneUnits(activeCountry.code)
      .then((rows) => {
        if (cancelled) return;
        setUnits(
          rows.map((r) => ({
            value: r.admin_one_name,
            label: r.admin_one_name,
            code: r.admin_one_code,
            bounds: r.admin_one_bounds,
          })),
        );
      })
      .catch(() => {
        if (!cancelled) setUnits([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingUnits(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeCountry?.code]);

  const countryOptions = useMemo(
    () => countries.map((c) => ({ value: c.code, label: c.name, country: c })),
    [countries],
  );
  const currentCountryOption = useMemo(
    () => countryOptions.find((o) => String(o.value) === String(activeCountry?.code)) || null,
    [countryOptions, activeCountry?.code],
  );

  // Cold open of a shared link has no country bbox; backfill it from the list.
  useEffect(() => {
    if (activeCountry?.code && !activeCountry.bounds && currentCountryOption?.country?.bounds) {
      onChange({ country: currentCountryOption.country });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCountryOption, activeCountry?.code, activeCountry?.bounds]);

  const isRegional = scope === "regional";

  const handleScope = (nextScope) => {
    if (nextScope === scope) return;
    if (nextScope === "regional" && units.length === 0) return;
    onChange({ scope: nextScope, adminOne: nextScope === "national" ? null : adminOne });
  };

  const handleCountry = (option) => {
    if (!option || String(option.value) === String(activeCountry?.code)) return;
    onChange({ country: option.country, adminOne: null, scope: "national" });
  };

  const handleAdminOne = (option) => {
    onChange({ adminOne: option || null });
  };

  return (
    <div className="report-controls">
      <div className="report-controls__field">
        <span className="report-controls__label">Reporting unit</span>
        <div className="report-controls__radios">
          <label className={`report-radio ${scope === "national" ? "is-active" : ""}`}>
            <input
              type="radio"
              name="reporting-unit"
              checked={scope === "national"}
              onChange={() => handleScope("national")}
            />
            National
          </label>
          <label className={`report-radio ${isRegional ? "is-active" : ""}`}>
            <input
              type="radio"
              name="reporting-unit"
              checked={isRegional}
              onChange={() => handleScope("regional")}
              disabled={units.length === 0}
            />
            Regional
          </label>
        </div>
      </div>

      <div className="report-controls__field">
        <span className="report-controls__label">
          {isRegional ? "Region (admin 1)" : "Country"}
        </span>
        {isRegional ? (
          <Select
            classNamePrefix="report-select"
            styles={SELECT_STYLES}
            options={units}
            value={adminOne}
            onChange={handleAdminOne}
            isClearable
            isLoading={loadingUnits}
            placeholder={units.length ? "Select a region…" : "No admin-1 units"}
            noOptionsMessage={() => "No admin-1 units"}
          />
        ) : (
          <Select
            classNamePrefix="report-select"
            styles={SELECT_STYLES}
            options={countryOptions}
            value={currentCountryOption}
            onChange={handleCountry}
            placeholder="Select a country…"
            isLoading={countryOptions.length === 0}
          />
        )}
        <div className="report-controls__hint">
          {isRegional
            ? adminOne
              ? `Focused on ${adminOne.label}, ${activeCountry?.name}`
              : `${activeCountry?.name}: pick a region to focus the analysis`
            : `National report for ${activeCountry?.name || "the selected country"}`}
        </div>
      </div>
    </div>
  );
}
